// Info: Booking Accept is a reworked version of the Accept Block component,
// in which the form is static
import React, { Dispatch, FC, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  AcceptingDriveData,
  IBookingDetailsApi,
  FacilityDriver,
  FacilityVehicle,
  MappedDrive,
} from '@common/interfaces';
import { ArrowRound } from '@assets/svg/icons';
import AcceptForm from '../AcceptForm';
import css from './styles.module.scss';

const DATE_FORMAT_FULL = 'YYYY-MM-DD HH:mm:ss';
const DATE_FORMAT_TEXT = 'DD MMM YYYY, HH:mm';

interface IBookingAccept {
  acceptingDriveData: AcceptingDriveData[];
  bookingData: IBookingDetailsApi;
  drivers: FacilityDriver[];
  totalDrives: MappedDrive[];
  vehicles: FacilityVehicle[];
  rejectBooking: (value: string) => Promise<void>;
  setAcceptingDriveData: Dispatch<SetStateAction<AcceptingDriveData[]>>;
  setCurrentDate: Dispatch<SetStateAction<string | undefined>>;
}

const BookingAccept: FC<IBookingAccept> = ({
  acceptingDriveData,
  bookingData,
  drivers,
  totalDrives,
  vehicles,
  rejectBooking,
  setAcceptingDriveData,
  setCurrentDate,
}) => {
  const { t, i18n } = useTranslation();

  const {
    agency_fk,
    recurring,
    routes,
    type_of_trip_display,
    first_name,
    uuid,
    last_name,
    n_of_passengers,
  } = bookingData;

  let accepted = 0;

  return (
    <section className={css.section}>
      <div className={css.info}>
        <div className={css.cell}>
          <div className={css.label}>{t('common.firstName')}</div>
          <p className={css.text}>{first_name}</p>
        </div>

        <div className={css.cell}>
          <div className={css.label}>{t('common.lastName')}</div>
          <p className={css.text}>{last_name}</p>
        </div>

        <div className={css.cell}>
          <div className={css.label}>{t('common.agency')}</div>
          <p className={css.text}>{agency_fk?.short_name}</p>
        </div>

        <div className={css.cell}>
          <div className={css.label}>{t('common.pax')}</div>
          <p className={css.text}>{n_of_passengers}</p>
        </div>

        {routes.map((route, i) => (
          <div className={css.cell} key={route.uuid}>
            <div className={css.label}>
              {t('bookingDetails.leg')} {i + 1}
            </div>
            <p className={css.text}>
              {moment(`${route.pickup_date} ${route.pickup_time}`, DATE_FORMAT_FULL).format(
                DATE_FORMAT_TEXT,
              )}
            </p>
          </div>
        ))}

        <div className={css.cell}>
          <div className={css.label}>{t('common.transferType')}</div>
          <p className={css.text}>{type_of_trip_display}</p>
        </div>
      </div>

      {recurring ? (
        <div className={css.info}>
          <div className={css.cell}>
            <div className={css.label}>{t('common.recurring.isRecurring')}</div>
            <p className={css.text}>{t('common.yes')}</p>
          </div>
          <div className={css.cell}>
            <div className={css.label}>{t('common.recurring.type')}</div>
            <p className={css.text}>
              {recurring.frequency_display}
              {recurring.frequency_display === 'Daily' ? `(${recurring.weekdays_display})` : null}
            </p>
          </div>
          <div className={css.cell}>
            <div className={css.label}>{t('common.inputStartDate')}</div>
            <p className={css.text}>
              {moment(recurring.start_date).locale(i18n.language).format('DD MMM, YYYY')}
            </p>
          </div>
          <div className={css.cell}>
            <div className={css.label}>{t('common.inputEndDate')}</div>
            <p className={css.text}>
              {moment(recurring.end_date).locale(i18n.language).format('DD MMM, YYYY')}
            </p>
          </div>
        </div>
      ) : null}

      <div>
        <div className={css.routes}>
          {(acceptingDriveData.length === routes.length || acceptingDriveData?.[0]?.invalid) &&
            acceptingDriveData.map((item: AcceptingDriveData, idx: number) => {
              if (accepted) {
                return;
              }

              if (!item.accepted) {
                accepted += 1;
              }

              return (
                <AcceptForm
                  acceptingDriveData={acceptingDriveData}
                  drivers={drivers}
                  index={idx}
                  key={routes[idx].uuid}
                  route={routes[idx]}
                  totalDrives={totalDrives}
                  vehicles={vehicles}
                  rejectBooking={rejectBooking}
                  setAcceptingDriveData={setAcceptingDriveData}
                  setCurrentDate={setCurrentDate}
                />
              );
            })}
        </div>

        <Link className={css.link} to={`/bookings/${uuid}`}>
          {t('bookingDetails.viewBookingDetails')}
          <ArrowRound type="right" className={css.svg} />
        </Link>
      </div>
    </section>
  );
};

export default BookingAccept;
